// Course types used across admin, mentor, and student
export const DEFAULT_COURSE_TYPES = [
    'Crash',
    'Long Term',
    'Short Term',
    '2-Year Integrated',
    'Dropper',
] as const

export type CourseType = string // Allow custom types

export const SUBJECTS = ['Physics', 'Chemistry', 'Biology', 'Zoology', 'Botany', 'Complete NEET'] as const

export interface CourseModule {
    id: string
    title: string
    lectures: number
    hours: number
}

export interface AdminCourse {
    id: string
    title: string
    category: string // Complete Package, Physics, Chemistry, Biology
    type: string // Crash, Long Term, etc.
    instructors: { id?: string, name: string, title: string }[]
    rating: number
    reviews: number
    students: number
    lectures: number
    duration: string
    features: string[] // Live Classes, Doubt Clearing, etc.
    modules: CourseModule[]
    aboutInstructor: string
    studentsTaught?: string
    lastUpdated: string
    price: string
    originalPrice: string
    discountPercent?: number
    isActive: boolean
    mentorIds: string[]
    createdAt: string
}

export interface AdminMentor {
    id: string
    name: string
    email: string
    subject: string
    assignedCourses: string[] // course type ids/titles (e.g., Crash, Long Term)
    assignedBatches?: string[] // specific batches (e.g., NEET 2025 Batch A)
    assignedSpecificCourses?: string[] // specific course IDs or Titles
    isActive: boolean
    createdAt: string
}

export interface Coupon {
    id: string
    code: string
    type: 'percentage' | 'flat'
    value: number
    expiryDate: string
    usageLimit: number
    usageCount: number
    isActive: boolean
}

export interface AbroadCollege {
    id: string
    name: string
    country: string
    image: string
    ranking: string
    fees: string
    intake: string
    hostelFees: string
    eligibility: string
    teachingMedium: string
    nmcStatus: string
    brochureUrl: string
    galleryImages: string[] // Array of image URLs
    isActive: boolean
}

export interface CoachingTieUp {
    id: string
    name: string
    location: string // City
    image: string
    bannerUrl: string
    courses: string
    feesRange: string
    students: string
    rating: string
    isActive: boolean
}

export interface AbroadLead {
    id: string
    studentName: string
    email: string
    phone: string
    collegeId: string
    collegeName: string
    status: 'new' | 'contacted' | 'counselling' | 'applied' | 'admitted' | 'closed'
    assignedMentorId: string | null
    assignedMentorName: string | null
    message?: string // Extra details from form
    createdAt: string
}

export interface PartnerLead {
    id: string
    studentName: string
    email: string
    phone: string
    instituteId: string
    instituteName: string
    status: 'new' | 'contacted' | 'counselling' | 'enrolled' | 'closed'
    assignedMentorId: string | null
    assignedMentorName: string | null
    courseInterest?: string
    createdAt: string
}

export interface PaymentRecord {
    id: string
    type: 'course' | 'test' | 'subscription'
    studentName: string
    amount: number
    date: string
    status: 'completed' | 'pending' | 'refunded' | 'refund_requested'
    refundReason?: string
}

// Mock data
export const mockAdminCourses: AdminCourse[] = [
    {
        id: 'c1', title: 'Complete NEET 2025 Preparation - Physics, Chemistry & Biology',
        category: 'Complete Package', type: 'Long Term',
        instructors: [{ id: 'm2', name: 'Dr. Rajesh Kumar', title: 'AIIMS Delhi, AIR 23' }],
        rating: 4.9, reviews: 450, students: 2500, lectures: 450, duration: '12 months',
        features: ['Live Classes', 'Doubt Clearing', 'Test Series', 'Study Material'],
        modules: [
            { id: 'm1', title: 'Module 1: Fundamentals', lectures: 12, hours: 3 },
            { id: 'm2', title: 'Module 2: Core Concepts', lectures: 15, hours: 4 },
            { id: 'm3', title: 'Module 3: Advanced Topics', lectures: 18, hours: 5 },
            { id: 'm4', title: 'Module 4: Practice & Revision', lectures: 10, hours: 2.5 },
        ],
        aboutInstructor: 'Expert Educator',
        studentsTaught: '2,500+ students taught',
        lastUpdated: 'January 2025',
        price: '₹19,999', originalPrice: '₹29,999', discountPercent: 33,
        isActive: true, mentorIds: ['m1'], createdAt: '2024-01-15',
    },
    {
        id: 'c2', title: 'Physics Masterclass', category: 'Physics', type: 'Crash',
        instructors: [{ id: 'm3', name: 'Prof. Anita Sharma', title: 'IIT Delhi' }],
        rating: 4.8, reviews: 320, students: 1800, lectures: 120, duration: '3 months',
        features: ['HD Videos', 'Practice Problems'], modules: [],
        aboutInstructor: 'IIT Delhi', lastUpdated: 'December 2024',
        price: '₹7,999', originalPrice: '₹12,999', isActive: true, mentorIds: ['m1'], createdAt: '2024-02-01'
    },
]

export const mockAdminMentors: AdminMentor[] = [
    { id: 'm1', name: 'Dr. Sarah Patel', email: 'sarah@ff.com', subject: 'Physics', assignedCourses: ['Long Term', 'Crash'], isActive: true, createdAt: '2024-01-01' },
    { id: 'm2', name: 'Dr. Rajesh Kumar', email: 'rajesh@ff.com', subject: 'Complete NEET', assignedCourses: ['Dropper'], isActive: true, createdAt: '2024-01-10' },
    { id: 'm3', name: 'Prof. Anita Sharma', email: 'anita@ff.com', subject: 'Chemistry', assignedCourses: [], isActive: true, createdAt: '2024-02-01' },
    { id: 'm4', name: 'Dr. Meera Nair', email: 'meera@ff.com', subject: 'Botany', assignedCourses: [], isActive: true, createdAt: '2024-02-15' },
    { id: 'm5', name: 'Dr. Anil Verma', email: 'anil@ff.com', subject: 'Zoology', assignedCourses: [], isActive: true, createdAt: '2024-02-15' },
    { id: 'm5', name: 'Dr. Anil Verma', email: 'anil@ff.com', subject: 'Zoology', assignedCourses: [], isActive: true, createdAt: '2024-02-15' },
]

export interface AdminCounsellor {
    id: string
    name: string
    email: string
    phone: string
    activeLeads: number
    totalLeads: number
    status: 'active' | 'inactive'
    createdAt: string
}

export const mockCounsellors: AdminCounsellor[] = [
    { id: 'counsellor-001', name: 'Counsellor', email: 'c@c', phone: '9876543210', activeLeads: 12, totalLeads: 45, status: 'active', createdAt: '2025-01-01' },
    { id: 'counsellor-002', name: 'Priya Sharma', email: 'priya.s@neet.com', phone: '9876543211', activeLeads: 8, totalLeads: 20, status: 'active', createdAt: '2025-02-01' },
]

export const mockAbroadColleges: AbroadCollege[] = [
    {
        id: 'ab1', name: 'Kazan Federal University', country: 'Russia',
        image: 'https://images.unsplash.com/photo-1562774053-701939374585?w=400&h=300&fit=crop',
        ranking: '#15 in Russia', fees: '₹4.5 Lakhs/year', intake: 'September',
        hostelFees: '₹50,000/year', eligibility: '50% in PCB', teachingMedium: 'English', nmcStatus: 'Approved',
        brochureUrl: '#', galleryImages: [], isActive: true
    },
    {
        id: 'ab2', name: 'Wuhan University', country: 'China',
        image: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=400&h=300&fit=crop',
        ranking: '#8 in China', fees: '₹5.2 Lakhs/year', intake: 'September',
        hostelFees: '₹60,000/year', eligibility: '60% in PCB', teachingMedium: 'English', nmcStatus: 'Approved',
        brochureUrl: '#', galleryImages: [], isActive: true
    },
]

export const mockCoachingTieUps: CoachingTieUp[] = [
    {
        id: 't1', name: 'Aakash Institute', location: 'Multiple Locations',
        image: 'https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?w=400&h=300&fit=crop',
        bannerUrl: 'https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?w=1200&h=400&fit=crop',
        courses: 'NEET, JEE', feesRange: '₹1L - ₹2L',
        students: '5,000+', rating: '4.8', isActive: true
    },
    {
        id: 't2', name: 'Allen Career Institute', location: 'Kota',
        image: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=400&h=300&fit=crop',
        bannerUrl: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=1200&h=400&fit=crop',
        courses: 'NEET, JEE, Foundation', feesRange: '₹1.5L - ₹2.5L',
        students: '3,500+', rating: '4.9', isActive: true
    },
]

export const mockAbroadLeads: AbroadLead[] = [
    { id: 'l1', studentName: 'Rahul Verma', email: 'rahul@email.com', phone: '9876543210', collegeId: 'ab1', collegeName: 'Kazan Federal University', status: 'counselling', assignedMentorId: 'm1', assignedMentorName: 'Dr. Sarah Patel', message: 'Interested in MBBS', createdAt: '2025-01-10' },
    { id: 'l2', studentName: 'Priya Nair', email: 'priya@email.com', phone: '9876543211', collegeId: 'ab2', collegeName: 'Wuhan University', status: 'new', assignedMentorId: null, assignedMentorName: null, message: 'Details about fees requested', createdAt: '2025-02-01' },
    { id: 'l3', studentName: 'Sneha Gupta', email: 'sneha@email.com', phone: '9876543212', collegeId: 'ab1', collegeName: 'Kazan Federal University', status: 'contacted', assignedMentorId: 'counsellor-001', assignedMentorName: 'Counsellor', message: 'Callback requested', createdAt: '2025-02-05' },
]

export const mockPartnerLeads: PartnerLead[] = [
    { id: 'pl1', studentName: 'Amit Kumar', email: 'amit@test.com', phone: '9000000000', instituteId: 't1', instituteName: 'Aakash Institute', status: 'new', assignedMentorId: null, assignedMentorName: null, courseInterest: 'NEET Dropper', createdAt: '2025-02-08' }
]

export const mockPayments: PaymentRecord[] = [
    { id: 'p1', type: 'course', studentName: 'Arun K', amount: 19999, date: '2025-02-01', status: 'completed' },
    { id: 'p2', type: 'test', studentName: 'Priya S', amount: 999, date: '2025-02-05', status: 'completed' },
    { id: 'p3', type: 'course', studentName: 'Rahul V', amount: 7999, date: '2025-02-06', status: 'pending' },
]

export const mockCoupons: Coupon[] = [
    { id: 'cp1', code: 'NEET2025', type: 'percentage', value: 10, expiryDate: '2025-12-31', usageLimit: 100, usageCount: 45, isActive: true },
    { id: 'cp2', code: 'WELCOME500', type: 'flat', value: 500, expiryDate: '2025-06-30', usageLimit: 50, usageCount: 12, isActive: true },
    { id: 'cp3', code: 'EARLYBIRD', type: 'percentage', value: 15, expiryDate: '2025-03-01', usageLimit: 200, usageCount: 150, isActive: false },
]

// ============================================
// SALES DASHBOARD MOCK DATA
// ============================================

export interface TopSellingCourse {
    id: string
    title: string
    category: string
    type: string
    studentsEnrolled: number
    revenue: number
    conversionRate: number
    rating: number
    trend: 'up' | 'down' | 'stable'
    trendPercent: number
}

export interface MonthlyEnrollment {
    month: string
    shortMonth: string
    enrollments: number
    revenue: number
}

export interface ConversionFunnel {
    stage: string
    count: number
    color: string
}

export const mockTopSellingCourses: TopSellingCourse[] = [
    { id: 'c1', title: 'Complete NEET 2025 Preparation', category: 'Complete Package', type: 'Long Term', studentsEnrolled: 2500, revenue: 4997500, conversionRate: 32.5, rating: 4.9, trend: 'up', trendPercent: 22 },
    { id: 'c2', title: 'Physics Masterclass', category: 'Physics', type: 'Crash', studentsEnrolled: 1800, revenue: 1439820, conversionRate: 28.3, rating: 4.8, trend: 'up', trendPercent: 15 },
    { id: 'c3', title: 'Chemistry Crash Course', category: 'Chemistry', type: 'Crash', studentsEnrolled: 1450, revenue: 1015500, conversionRate: 24.1, rating: 4.7, trend: 'stable', trendPercent: 3 },
    { id: 'c4', title: 'Biology Deep Dive', category: 'Biology', type: 'Long Term', studentsEnrolled: 1200, revenue: 1559880, conversionRate: 21.7, rating: 4.8, trend: 'up', trendPercent: 18 },
    { id: 'c5', title: 'NEET Dropper Batch 2025', category: 'Complete Package', type: 'Dropper', studentsEnrolled: 980, revenue: 1959802, conversionRate: 35.2, rating: 4.9, trend: 'up', trendPercent: 28 },
    { id: 'c6', title: '2-Year Integrated Program', category: 'Complete Package', type: '2-Year Integrated', studentsEnrolled: 750, revenue: 2624250, conversionRate: 18.5, rating: 4.6, trend: 'down', trendPercent: -5 },
    { id: 'c7', title: 'Short Term Revision', category: 'Complete Package', type: 'Short Term', studentsEnrolled: 620, revenue: 309380, conversionRate: 42.1, rating: 4.5, trend: 'up', trendPercent: 35 },
    { id: 'c8', title: 'Botany Essentials', category: 'Biology', type: 'Short Term', studentsEnrolled: 480, revenue: 239520, conversionRate: 19.8, rating: 4.4, trend: 'stable', trendPercent: 1 },
]

export const mockMonthlyEnrollments: MonthlyEnrollment[] = [
    { month: 'September 2024', shortMonth: 'Sep', enrollments: 320, revenue: 1920000 },
    { month: 'October 2024', shortMonth: 'Oct', enrollments: 450, revenue: 2700000 },
    { month: 'November 2024', shortMonth: 'Nov', enrollments: 580, revenue: 3480000 },
    { month: 'December 2024', shortMonth: 'Dec', enrollments: 720, revenue: 4320000 },
    { month: 'January 2025', shortMonth: 'Jan', enrollments: 890, revenue: 5340000 },
    { month: 'February 2025', shortMonth: 'Feb', enrollments: 1050, revenue: 6300000 },
    { month: 'March 2025', shortMonth: 'Mar', enrollments: 1240, revenue: 7440000 },
]

export const mockConversionFunnel: ConversionFunnel[] = [
    { stage: 'Website Visitors', count: 45000, color: 'from-blue-400 to-blue-500' },
    { stage: 'Sign Ups', count: 12500, color: 'from-violet-400 to-violet-500' },
    { stage: 'Free Trial Started', count: 8200, color: 'from-purple-400 to-purple-500' },
    { stage: 'Course Purchased', count: 5800, color: 'from-emerald-400 to-emerald-500' },
    { stage: 'Course Completed', count: 3200, color: 'from-amber-400 to-amber-500' },
]

// ============================================
// LOGIN DATA MOCK DATA
// ============================================

export interface LoginRecord {
    id: string
    userId: string
    name: string
    email: string
    role: 'student' | 'mentor' | 'counsellor' | 'admin' | 'sub-admin'
    lastLogin: string
    loginCount: number
    device: string
    browser: string
    ipAddress: string
    location: string
    status: 'online' | 'offline' | 'idle'
    accountStatus: 'active' | 'suspended' | 'deactivated'
    joinedDate: string
}

export const mockLoginData: LoginRecord[] = [
    { id: 'lr-001', userId: 'st-001', name: 'Arun Kumar', email: 'arun@example.com', role: 'student', lastLogin: '2025-03-31T12:30:00', loginCount: 145, device: 'Windows PC', browser: 'Chrome 122', ipAddress: '192.168.1.45', location: 'Mumbai, IN', status: 'online', accountStatus: 'active', joinedDate: '2024-06-15' },
    { id: 'lr-002', userId: 'st-002', name: 'Priya Sharma', email: 'priya@example.com', role: 'student', lastLogin: '2025-03-31T11:15:00', loginCount: 198, device: 'iPhone 15', browser: 'Safari 17', ipAddress: '103.21.45.78', location: 'Delhi, IN', status: 'online', accountStatus: 'active', joinedDate: '2024-05-20' },
    { id: 'lr-003', userId: 'st-003', name: 'Rahul Verma', email: 'rahul@example.com', role: 'student', lastLogin: '2025-03-29T18:45:00', loginCount: 67, device: 'Android Phone', browser: 'Chrome Mobile', ipAddress: '49.36.12.99', location: 'Bangalore, IN', status: 'offline', accountStatus: 'active', joinedDate: '2024-08-10' },
    { id: 'lr-004', userId: 'st-004', name: 'Ananya Patel', email: 'ananya@example.com', role: 'student', lastLogin: '2025-03-31T13:00:00', loginCount: 210, device: 'MacBook Pro', browser: 'Safari 17', ipAddress: '59.92.78.34', location: 'Ahmedabad, IN', status: 'online', accountStatus: 'active', joinedDate: '2024-04-01' },
    { id: 'lr-005', userId: 'st-005', name: 'Rohan Singh', email: 'rohan@example.com', role: 'student', lastLogin: '2025-03-31T09:20:00', loginCount: 132, device: 'Windows PC', browser: 'Firefox 123', ipAddress: '182.73.45.12', location: 'Jaipur, IN', status: 'idle', accountStatus: 'active', joinedDate: '2024-07-01' },
    { id: 'lr-006', userId: 'st-006', name: 'Sneha Gupta', email: 'sneha@example.com', role: 'student', lastLogin: '2025-03-30T20:10:00', loginCount: 89, device: 'iPad Air', browser: 'Safari 17', ipAddress: '223.226.78.56', location: 'Pune, IN', status: 'offline', accountStatus: 'active', joinedDate: '2024-09-15' },
    { id: 'lr-007', userId: 'st-007', name: 'Vikas Reddy', email: 'vikas@example.com', role: 'student', lastLogin: '2025-03-31T13:45:00', loginCount: 175, device: 'Android Phone', browser: 'Chrome Mobile', ipAddress: '157.49.23.67', location: 'Hyderabad, IN', status: 'online', accountStatus: 'active', joinedDate: '2024-05-10' },
    { id: 'lr-008', userId: 'mentor-001', name: 'Dr. Sarah Johnson', email: 'sarah.johnson@neet.edu', role: 'mentor', lastLogin: '2025-03-31T10:00:00', loginCount: 320, device: 'MacBook Pro', browser: 'Chrome 122', ipAddress: '106.51.34.89', location: 'Chennai, IN', status: 'online', accountStatus: 'active', joinedDate: '2024-01-15' },
    { id: 'lr-009', userId: 'mentor-002', name: 'Prof. Rajesh Kumar', email: 'rajesh.kumar@neet.edu', role: 'mentor', lastLogin: '2025-03-31T08:30:00', loginCount: 280, device: 'Windows PC', browser: 'Edge 122', ipAddress: '14.139.56.78', location: 'Delhi, IN', status: 'idle', accountStatus: 'active', joinedDate: '2024-01-20' },
    { id: 'lr-010', userId: 'mentor-003', name: 'Dr. Meera Patel', email: 'meera.patel@neet.edu', role: 'mentor', lastLogin: '2025-03-30T16:20:00', loginCount: 245, device: 'iPad Pro', browser: 'Safari 17', ipAddress: '122.168.12.45', location: 'Mumbai, IN', status: 'offline', accountStatus: 'active', joinedDate: '2024-02-01' },
    { id: 'lr-011', userId: 'counsellor-001', name: 'Counsellor One', email: 'c@c', role: 'counsellor', lastLogin: '2025-03-31T11:45:00', loginCount: 156, device: 'Windows PC', browser: 'Chrome 122', ipAddress: '203.115.67.89', location: 'Kota, IN', status: 'online', accountStatus: 'active', joinedDate: '2025-01-01' },
    { id: 'lr-012', userId: 'counsellor-002', name: 'Priya Sharma (Counsellor)', email: 'priya.s@neet.com', role: 'counsellor', lastLogin: '2025-03-29T14:30:00', loginCount: 78, device: 'Android Phone', browser: 'Chrome Mobile', ipAddress: '117.239.45.23', location: 'Lucknow, IN', status: 'offline', accountStatus: 'active', joinedDate: '2025-02-01' },
    { id: 'lr-013', userId: 'admin-001', name: 'Admin User', email: 'admin@ff.com', role: 'admin', lastLogin: '2025-03-31T13:55:00', loginCount: 450, device: 'MacBook Pro', browser: 'Chrome 122', ipAddress: '10.0.0.1', location: 'Mumbai, IN', status: 'online', accountStatus: 'active', joinedDate: '2024-01-01' },
    { id: 'lr-014', userId: 'st-008', name: 'Kavya Joshi', email: 'kavya@example.com', role: 'student', lastLogin: '2025-03-28T22:15:00', loginCount: 42, device: 'Windows PC', browser: 'Chrome 122', ipAddress: '45.119.78.90', location: 'Nagpur, IN', status: 'offline', accountStatus: 'suspended', joinedDate: '2024-10-01' },
    { id: 'lr-015', userId: 'st-009', name: 'Arjun Mehta', email: 'arjun@example.com', role: 'student', lastLogin: '2025-03-25T15:00:00', loginCount: 23, device: 'Android Phone', browser: 'Chrome Mobile', ipAddress: '103.45.67.12', location: 'Kolkata, IN', status: 'offline', accountStatus: 'deactivated', joinedDate: '2024-11-15' },
]
